import {
  formError,
  formFunction,
  formIneligible,
  formInit,
  formLoad,
  formNew,
  formStart,
  formUpdate,
  getDataFromPath,
  isSaveChallengeRequired,
  setDataFromPath,
  userCancel,
  userSave,
  userSubmit
} from "@transact-open-ux/core";
import React, {
  ComponentType,
  ConsumerProps,
  FunctionComponent,
  useContext,
  useEffect,
  useReducer
} from "react";

type ValidationError = Array<{ [errorKey: string]: string }>;

export interface TransactProviderProps {
  children?: React.ReactNode;
  /**
   * If save challenge is required and this function prop exists, it will be called.
   * An example would be to open a save challenge modal dialog.
   */
  onSaveChallengeRequired?: () => void;
  /**
   * If set, this prop specifies the globally unique application form code to create a new transaction for with a `formNew` call.
   * **To be used when Transact is NOT rendering the Open UX application.**
   */
  formCode?: string;
  /**
   * Used in conjunction with the `formCode` prop.
   * If set, this prop specifies the application form version number to use, if not specified then the current form version will be used for the transaction
   * **To be used when Transact is NOT rendering the Open UX application.**
   */
  formVersion?: string;
  /**
   * This prop specifies the endpoint which will be used for the `formNew` call and all subsequent Open UX API calls
   * **To be used when Transact is NOT rendering the Open UX application.**
   */
  endpoint?: string;
  /**
   * This prop specifies the tracking code or reference number. This value should be used when opening an previously saved form application.
   * **To be used when Transact is NOT rendering the Open UX application.**
   */
  trackingCode?: string;
}

export interface TransactProviderState {
  /**
   * Sets the loading state as well as updating the transact state on return of the call
   * @param transactCall - A promise returned from a call
   */
  dispatchCall?: (transactCall: Promise<any>) => Promise<any>;
  /**
   * Contains any error content if an error is returned
   */
  error?: object;
  /**
   * Contains the formData object, this can be used to set the initial values of your form.
   * Ensure to re-initialize your values so your form will update.<br>
   * *Note: This props will be out of sync with your form values and should only be used for initialization / updating your form from the server.*
   */
  formData?: object;
  /**
   * This command type enables recording information about an unexpected JavaScript error in the application
   * and storing this information in the Transact Manager Error Log for operational support.
   */
  formError?: typeof formError;
  /**
   * This command is used to call an arbitrary Fluent Function for performing server side logic.
   */
  formFunction?: typeof formFunction;
  /**
   * Make a call to the form ineligible operation.
   * The app should display a custom page after this operation preventing the user from performing further work.
   */
  formIneligible?: typeof formIneligible;
  /**
   * This command type specifies that the form application is fully initialized and ready for user interaction.
   * The role of this command to provide analytics around the performance of forms and understand how long they take to open,
   * and what the impact is on conversion rate and form starts.
   */
  formInit?: typeof formInit;
  /**
   * Make a call to the form load operation to load the application prefill data at load time.
   * You can also load a saved application via a combination of parameters.
   * After the data has been retrieved a form initiated operation will be called.
   */
  formLoad?: typeof formLoad;
  /**
   * A boolean value that indicates whether the form has loaded or not.
   * A formLoad call will set this to true
   */
  formLoaded?: boolean;
  /**
   * The formNew command creates a new form transaction or opens an existing transaction.
   * This call should be followed by the formLoad command.
   * <br>
   * This command is provided to support the use case where Transact is not rendering the HTML Open UX application,
   * but instead it is being rendered separately by another system such as a Content Management System (CSM) or another application.
   */
  formNew?: (
    requestUrl: string,
    formCode?: string,
    formVersion?: string,
    trackingCode?: string
  ) => Promise<any>;
  /**
   * Make a call to the form start operation specifying that the user has started interacting with the form.
   */
  formStart?: typeof formStart;
  /**
   * Represents the formStatus of the application.
   */
  formStatus?:
    | "Assigned"
    | "Opened"
    | "Saved"
    | "Submitted"
    | "Completed"
    | "Expired"
    | "Abandoned";
  /**
   * Make a call to the form update operation, this performs an app initiated background save.
   * Reasons to call this command include: page change save, timer based save, milestone based save, add attachment,
   * specify attachment to be added manually and remove attachment.
   */
  formUpdate?: typeof formUpdate;
  /**
   * Returns the formApi which has been set by `setFormApi`.
   * This is to de-couple the `TransactProvider` from a specific form library.
   * By default we use `Formik` in our TransactForm.
   */
  getFormApi?: () => any;
  /**
   * Returns the Insights Profile if Insights is enabled, otherwise it will return undefined
   */
  getInsightsProfile?: () => object | undefined;
  /**
   * Returns the narrative data object (if it exists), these values can be used to drive logic in your application
   * @param path
   */
  getNarrativeData?: (path?: string) => any;
  /**
   * Returns the server side error object (if it exists)
   * @param errorKey
   */
  getValidationError?: (errorKey: string) => object | undefined;
  /**
   * Remove server side validation messages from the array of validationErrors.
   * Specify the errors via the key or keys e.g. removeValidationError("stdErrs.formSaveChallengeInvalidAnswer");
   * @param errorKeys
   */
  removeValidationErrors?: (errorKeys: string[] | string) => void;
  /**
   * A helper method to perform a save challenge request.
   * If successful, a `SaveChallengeSuccess` boolean value will be returned by the call
   */
  resumeForm?: typeof formLoad;
  /**
   * Shows the number of times the current application state has been saved before
   */
  revisionNumber?: number;
  /**
   * A boolean property that indicates whether a transact call is in progress
   */
  isLoading?: boolean;
  /**
   * A boolean property that indicates whether a save challenge call is in progress
   */
  saveChallengeLoading?: boolean;
  /**
   * Sets the formApi object to the Transact Context
   * @param formApi
   */
  setFormApi?: (formApi: object) => void;
  /**
   * Make a call that performs a user initiated cancel abandonment operation.
   * The app should display a cancel confirmation page after this operation.
   */
  userCancel?: typeof userCancel;
  /**
   * Make a call that performs a user initiated save operation.
   * It is recommended that the app displays a save confirmation page after this operation.
   */
  userSave?: typeof userSave;
  /**
   * Make a call that performs a user initiated submit operation.
   * The app should display a submission confirmation page after this operation.
   */
  userSubmit?: typeof userSubmit;
  /**
   * A method that will update the narrative data
   * @param path
   * @param value
   */
  updateNarrativeData?: (path: string, value: any) => void;
  /**
   * A list of validation errors returned by the server
   * <br>
   * A validation error object can consist of the following keys
   * <br>
   * **path** -  which specified the XPath of the data element with the validation error
   * **errorKey** - which specifies the localized error message key
   * **parameters** - specified optional parameters which could be displayed in a localized error message
   */
  validationErrors?: ValidationError;
}

const TransactContext = React.createContext({});

const initialState: TransactProviderState = {
  error: null,
  formData: {},
  formError: null,
  formLoaded: false,
  isLoading: false,
  revisionNumber: 0,
  saveChallengeLoading: false,
  validationErrors: []
};

interface NarrativeData {
  path: string;
  value: any;
}

type TransactAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_FORM_LOADED" }
  | { type: "SET_NARRATIVE_DATA"; payload: NarrativeData }
  | { type: "SET_SAVE_CHALLENGE_LOADING"; payload: boolean }
  | { type: "SET_VALIDATION_ERRORS"; payload: ValidationError }
  | { type: "SET_RESPONSE_STATE"; payload: object }
  | { type: "SET_STATE"; payload: object };

const reducer = (state: TransactProviderState, action: TransactAction) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_FORM_LOADED":
      return { ...state, formLoaded: true, saveChallengeLoading: false };
    case "SET_NARRATIVE_DATA":
      return {
        ...state,
        formData: setDataFromPath(
          `SFMData.NarrativeMeta.${action.payload.path}`,
          state.formData,
          action.payload.value
        )
      };
    case "SET_SAVE_CHALLENGE_LOADING":
      return { ...state, saveChallengeLoading: action.payload };
    case "SET_VALIDATION_ERRORS":
      return { ...state, validationErrors: action.payload };
    case "SET_RESPONSE_STATE":
      return { ...state, ...action.payload, isLoading: false };
    case "SET_STATE":
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

/**
 * A component that surrounds your entire app and handles all the transact API logic and state.
 *
 * ## Example
 * Wrapping your root App component
 * ```jsx
 * import { TransactProvider, PageProvider } from '@transact-open-ux/react';
 * import App from './App';
 *
 * ReactDOM.render(
 *  <TransactProvider>
 *    <PageProvider>
 *     <App />
 *    </PageProvider>
 *  </TransactProvider>,
 * document.getElementById('root')
 * );
 * ```
 */
const TransactProvider: FunctionComponent<TransactProviderProps> = ({
  children,
  onSaveChallengeRequired,
  formCode,
  formVersion,
  endpoint: formNewEndpoint,
  trackingCode: formNewTrackingCode
}: TransactProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  let formApi;

  async function init() {
    try {
      if (formNewEndpoint && (formCode || formNewTrackingCode)) {
        await formNew(
          formNewEndpoint,
          formCode,
          formVersion,
          formNewTrackingCode
        );
      }

      if (isSaveChallengeRequired()) {
        if (typeof onSaveChallengeRequired === "function") {
          onSaveChallengeRequired();
        }
        // treat save challenge required as loaded form, to load children of TransactForm
        dispatch({
          type: "SET_FORM_LOADED"
        });
      } else {
        await handleFormLoad();
      }
    } catch (e) {
      dispatch({
        payload: e,
        type: "SET_RESPONSE_STATE"
      });
    }
  }

  /**
   * Sets the loading properties as well as updating the transact state
   * @param transactCall - A promise returned from a call
   */
  async function dispatchCall(transactCall: Promise<object>) {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const response = await transactCall;
      dispatch({
        payload: response,
        type: "SET_RESPONSE_STATE"
      });

      return response;
    } catch (e) {
      dispatch({
        payload: e,
        type: "SET_RESPONSE_STATE"
      });

      return e;
    }
  }

  const getInsightsProfile = () =>
    getDataFromPath("SFMData.SystemProfile.Insights", state.formData);

  const getNarrativeData = (path?: string) =>
    getDataFromPath(
      `SFMData.NarrativeMeta${path ? "." + path : ""}`,
      state.formData
    );

  const getValidationError = (errorKey: string) => {
    const { validationErrors } = state;

    const error = validationErrors.filter(
      ({ errorKey: err }) => errorKey === err
    );

    return error.length ? error[0] : undefined;
  };

  const updateNarrativeData = (path: string, value: any) => {
    dispatch({
      payload: {
        path,
        value
      },
      type: "SET_NARRATIVE_DATA"
    });
  };

  async function handleFormLoad(...args) {
    try {
      const response = await dispatchCall(formLoad(...args));
      dispatch({ type: "SET_FORM_LOADED" });
      return response;
    } catch (e) {
      return e;
    }
  }

  async function handleTransactCall(fn, successPage, ...args) {
    const response = await dispatchCall(fn(...args));
    if (successPage) {
      updateNarrativeData("CurrentPage", successPage);
    }
    return response;
  }

  const removeValidationErrors = (errorKeys: string[] | string) => {
    const { validationErrors } = state;
    if (typeof errorKeys === "string") {
      errorKeys = [errorKeys];
    }
    const newValidationErrors = validationErrors.filter(
      ({ errorKey }) => errorKeys.indexOf(errorKey) === -1
    );

    if (newValidationErrors.length !== validationErrors.length) {
      dispatch({ type: "SET_VALIDATION_ERRORS", payload: newValidationErrors });
    }
  };

  async function resumeForm(
    saveChallengeAnswer?: string,
    trackingCode?: string,
    parameters?: object
  ) {
    dispatch({ type: "SET_SAVE_CHALLENGE_LOADING", payload: true });
    const response = await handleFormLoad(
      saveChallengeAnswer,
      trackingCode,
      parameters
    );
    const { validationErrors, formData: newFormData } = response;
    if (!validationErrors && typeof newFormData === "object") {
      removeValidationErrors([
        "stdErrs.formSaveChallengeInvalidAnswer",
        "stdErrs.formTrackingCodeNotFound"
      ]);
      return {
        ...response,
        SaveChallengeSuccess: true
      };
    }
    return response;
  }

  useEffect(() => {
    init();
  }, []);

  const ctx: TransactProviderState = {
    ...state,
    formError: (stacktrace: string, context?: string) =>
      dispatchCall(formError(stacktrace, context)),
    formFunction,
    formIneligible: (data, ...args) =>
      dispatchCall(formIneligible(data, ...args)),
    formInit: () => dispatchCall(formInit()),
    formLoad: handleFormLoad,
    formNew: (endpoint, ...args) => dispatchCall(formNew(endpoint, ...args)),
    formStart: (data, ...args) => dispatchCall(formStart(data, ...args)),
    formUpdate: (data, ...args) => dispatchCall(formUpdate(data, ...args)),
    getFormApi: () => formApi,
    getInsightsProfile,
    getNarrativeData,
    getValidationError,
    resumeForm,
    setFormApi: (formApiObj: object) => {
      formApi = formApiObj;
    },
    userCancel: (data, milestone?: string) =>
      handleTransactCall(userCancel, "CancelSuccess", data, milestone),
    userSave: (data, ...args) =>
      handleTransactCall(userSave, "SaveSuccess", data, ...args),
    userSubmit: (data, ...args) =>
      handleTransactCall(userSubmit, "SubmitSuccess", data, ...args)
  };

  return (
    <TransactContext.Provider value={ctx}>{children}</TransactContext.Provider>
  );
};

/**
 * A component that can access the transact state and form api via render props from inside a `<TransactProvider/>` component.
 *
 * ## Example
 *
 * Creating a save button using render props.
 *
 * ```jsx
 * import { TransactConsumer } from '@transact-open-ux/react';
 *
 * export default function SaveButton() {
 *  return (
 *   <TransactConsumer>
 *     {({ userSave, getFormApi }) => (
 *       <button onClick={() => userSave(getFormApi().values)}>Save</button>
 *     )}
 *   </TransactConsumer>
 *  )
 * }
 * ```
 */
const TransactConsumer: ComponentType<ConsumerProps<TransactProviderState>> =
  TransactContext.Consumer;

const useTransactContext = () =>
  useContext<TransactProviderState>(TransactContext);

export { TransactConsumer, TransactProvider, useTransactContext };
